// STRATEGIST — standalone view of recently generated engagement-strategy
// briefs. Each row opens the SAME drawer the Prospects page uses, so the sales
// team can re-open a person's brief without re-importing. ADVISORY ONLY,
// human-executed, BACK_STAGE_ONLY. Briefs are generated from the per-prospect
// 'Strategy' action on /ops/prospects.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Sparkles, Inbox, Target } from "lucide-react";

import MockBadge from "@/components/MockBadge";
import { StrategyBriefDrawer } from "@/components/StrategyBrief";
import { strategist } from "@/lib/api";
import { fmtTime } from "@/lib/format";

function fitColor(score) {
  if (score == null) return "rgb(var(--muted-2))";
  if (score >= 75) return "rgb(32,138,78)";
  if (score >= 50) return "rgb(176,116,18)";
  return "rgb(196,52,44)";
}

function BriefRow({ b, onOpen }) {
  const name = b.full_name || b.name || b.target_name || "—";
  const company = b.company || b.organization || "—";
  const fit = b.fit_score ?? b.fit?.score ?? b.score;
  const when = b.created_at || b.updated_at || b.generated_at || null;
  const leadId = b.lead_id || b.id;
  return (
    <tr
      data-testid={`strategist-row-${b.id || leadId || name}`}
      className="border-t border-[rgb(var(--ln))] hover:bg-[rgb(236,229,216)] cursor-pointer"
      onClick={() => onOpen({ id: leadId, full_name: name, company })}
    >
      <td className="px-3 py-2.5 mono text-[12px] text-[rgb(var(--fg))] align-top">
        <span className="truncate block max-w-[220px]" title={name}>
          {name}
        </span>
      </td>
      <td className="px-3 py-2.5 mono text-[12px] text-[rgb(var(--dim))] align-top">
        <span className="truncate block max-w-[200px]" title={company}>
          {company}
        </span>
      </td>
      <td className="px-3 py-2.5 mono align-top">
        <span
          className="font-display text-[18px] leading-none"
          style={{ color: fitColor(typeof fit === "number" ? fit : null) }}
        >
          {typeof fit === "number" ? fit : "—"}
        </span>
      </td>
      <td className="px-3 py-2.5 mono text-[11px] text-[rgb(var(--muted-2))] align-top whitespace-nowrap">
        {when ? fmtTime(when) : "—"}
      </td>
      <td className="px-3 py-2.5 align-top text-right">
        <span className="mono inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] text-[rgb(var(--gold))]">
          <Sparkles size={11} /> Open
        </span>
      </td>
    </tr>
  );
}

export default function Strategist() {
  const [openLead, setOpenLead] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["strategist-briefs"],
    queryFn: () => strategist.list(),
    retry: false,
  });

  const rows = Array.isArray(data) ? data : data?.briefs || data?.items || [];

  return (
    <div data-testid="page-strategist" className="p-6 md:p-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div className="min-w-0">
          <div className="pj-label mb-2">· Outreach · Engagement strategy</div>
          <h1 className="font-display text-[32px] md:text-[40px] leading-[1.02]">Strategist</h1>
        </div>
        <div className="shrink-0">
          <MockBadge />
        </div>
      </div>

      <p className="mono text-[12px] text-[rgb(var(--dim))] mb-6 max-w-3xl" data-testid="strategist-blurb">
        Per-person engagement-strategy briefs — exactly how to approach each high-status prospect.
        Generate one from the <span className="text-[rgb(var(--gold))]">Strategy</span> action on a
        prospect row. Advisory only · human-executed · we never automate or scrape.
      </p>

      <div className="flex items-center gap-2 mb-3">
        <Target size={14} className="text-[rgb(var(--gold))] shrink-0" />
        <h2 className="mono text-[13px] uppercase tracking-[0.12em] text-[rgb(var(--fg))]">
          Recent briefs
        </h2>
      </div>

      {error && (
        <div className="pj-card p-6 mono text-[12px] text-[rgb(196,52,44)]" data-testid="strategist-load-error">
          Could not load briefs (admin / ops roles only).
        </div>
      )}
      {isLoading && !data && (
        <div className="pj-card p-6 mono text-[12px] text-[rgb(var(--dim))]">Loading briefs…</div>
      )}

      {data && rows.length === 0 && (
        <div
          className="pj-card p-8 flex flex-col items-center justify-center text-center"
          data-testid="strategist-empty"
        >
          <Inbox size={20} className="text-[rgb(var(--muted-2))] mb-2" />
          <div className="mono text-[12px] text-[rgb(var(--dim))]">
            No briefs yet — generate one from the Strategy action on a prospect.
          </div>
        </div>
      )}

      {data && rows.length > 0 && (
        <div className="pj-card overflow-x-auto" data-testid="strategist-table">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                {["Name", "Company", "Fit", "Generated", ""].map((h, i) => (
                  <th
                    key={i}
                    className={`px-3 py-2.5 mono text-[10px] uppercase tracking-[0.14em] text-[rgb(var(--muted-2))] font-normal ${
                      i === 4 ? "text-right" : ""
                    }`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <BriefRow key={b.id || b.lead_id || b.full_name} b={b} onOpen={setOpenLead} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openLead && <StrategyBriefDrawer lead={openLead} onClose={() => setOpenLead(null)} />}
    </div>
  );
}
